import { cloneDeep, isEmpty, isNil, mapValues, pick, sum } from 'lodash';
import { add, format } from 'date-fns';
import * as Yup from 'yup';
import { createMemoryHistory } from 'history';
import objectScan from 'object-scan';
import pluralize from 'pluralize';
import EcomEngineAPI from 'api/ecom_engine_api';
import {
  phoneNumber,
  phoneNumberLink,
  reservationCreations,
  trackError,
} from 'utils/helpers';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';

/**
 * Return corresponding action based on product type
 *
 *                     ┌-- Ship, ScheduledTripResource --> createScheduledTripReservation
 * product type is..?--├-- Package                     --> createPackageReservation
 *                     └-- GenericResource             --> createReservation
 *
 */
export const getReservationAction = (resource_id) =>
  EcomEngineAPI.resource
    .getResourceByID(resource_id)
    .then((res) => {
      const { type } = res;
      if (!type) {
        return Promise.reject(new Error('No type returned'));
      }
      const action = reservationCreations[type];
      return action;
    })
    .catch((error) => {
      trackError(error);
      Promise.reject(error);
    });

export const buildInvalidAndValidDates = (bookability) => {
  if (!bookability) {
    return {
      validDates: [],
      invalidDates: [],
    };
  }

  const allDays = Object.keys(bookability);

  return {
    validDates: allDays.filter((day) => bookability[day].available),
    invalidDates: allDays.filter((day) => !bookability[day].available),
  };
};

export const formatDate = (date) => (date ? format(date, FORMAT_QT_DATE) : '');

export const getInitialValues = ({
  packageOptions,
  passengerTypeIDs,
  bedRequirements,
  minPassenger,
  isGiftVoucher,
}) => {
  // [1 ,2, 3] => {1: 1, 2: 0, 3: 0}
  const initialPassengers = Object.fromEntries(
    passengerTypeIDs.map((p, i) => [p, i === 0 ? minPassenger : 0])
  );
  const values = {
    passengers: initialPassengers,
    pickUp: '',
    dropOff: '',
    // Use the first option as defalt if possible
    bedRequirement: bedRequirements?.[0]?.id || '',
    roomType: '',
    optionalExtras: {},
  };
  const { pickUps, dropOffs } = packageOptions || {};
  if (pickUps) {
    values.pickUp = pickUps.options[0].id;
  }
  if (dropOffs) {
    values.dropOff = dropOffs.options[0].id;
  }

  // For gift vocher, hide the calendar and set the date to tomorrow
  if (isGiftVoucher) {
    values.date = format(add(new Date(), { days: 1 }), FORMAT_QT_DATE);
  }

  return values;
};

export const getValidationRuls = ({
  minPassenger,
  maxPassenger,
  independentPassengerTypes,
  soloChildWarningMessage,
}) => {
  const formSchema = Yup.object().shape({
    date: Yup.string().required(),
    passengers: Yup.lazy((value) =>
      Yup.object(mapValues(value, () => Yup.number())).test({
        name: 'passenger-validation',
        // Dynamic yup error message
        // https://github.com/jquense/yup/issues/222
        test(passengers) {
          const count = sum(Object.values(passengers));

          if (count < minPassenger) {
            return this.createError({
              message: `You must have at least ${pluralize(
                'passenger',
                minPassenger,
                true
              )}`,
            });
          }

          if (maxPassenger && count > maxPassenger) {
            return this.createError({
              message: (
                <>
                  <p>
                    You’ve reached the limit of{' '}
                    {pluralize('passenger', maxPassenger, true)}.
                  </p>
                  <p>
                    To proceed with more passengers, please call us on{' '}
                    <a className="text-danger" href={`tel:${phoneNumberLink}`}>
                      {phoneNumber}
                    </a>
                    .
                  </p>
                </>
              ),
            });
          }

          if (!isEmpty(independentPassengerTypes)) {
            const elder = pick(passengers, independentPassengerTypes);
            if (sum(Object.values(elder)) <= 0) {
              return this.createError({
                message:
                  soloChildWarningMessage ||
                  'You must at least have one adult.',
              });
            }
          }

          return true;
        },
      })
    ),
  });

  return formSchema;
};

// Used in storybooks and testing tasks
// https://dev.to/wolverineks/react-router-testing-location-state-33fo
export const createHistory = (
  bookableOption = {
    title: 'From Adelaide with return coach and ferry',
    type: 'booking-option',
    price: '$188 per child',
    resource_id: 10426,
    description:
      '<p>Departs selected Adelaide CBD hotels or Adelaide Central Bus Station at 6:45am and Returns 10:30pm.</p>',
    passenger_types: [],
    price_definition: [],
    hasFerry: true,
    hasAccommodation: false,
  }
) => {
  const history = createMemoryHistory();
  const state = {
    bookableOption,
    back: false,
  };
  history.push('/', state);

  return history;
};

export const getPackageOptions = (resource) => {
  // When pick = 2, don’t let customer select the location. QT will automatically select one
  const PICK_FIRST_AVAILABLE = 2;
  const packageOptions = resource.package_options;
  if (isEmpty(packageOptions)) {
    return {};
  }

  const manualOptions = packageOptions.filter(
    (option) => option.resource_group.pick !== PICK_FIRST_AVAILABLE
  );

  let result = {};
  const pickUps = manualOptions.find(
    (option) => option.key === 'from_route_stop_id'
  );
  const dropOffs = manualOptions.find(
    (option) => option.key === 'to_route_stop_id'
  );
  if (pickUps) {
    result = Object.assign(result, { pickUps });
  }
  if (dropOffs) {
    result = Object.assign(result, { dropOffs });
  }

  return result;
};

export const noDefaultSelection = (group) =>
  group.package_items.find((item) => item.default_selection) === undefined;

export const isAccommdation = (group) =>
  group.package_items[0].resource.type === 'Accommodation';

const ROOM_PICKER_RESOURCE_TITLE = 'Room Picker';
const isRoomPickerResource = (item) =>
  item?.title === ROOM_PICKER_RESOURCE_TITLE;

export const getComplexApiData = (
  resource,
  complexDeparture,
  complexReturn,
  departureFerry,
  returnFerry,
  values
) => {
  const apiData = {
    resource_id: resource.id,
    first_travel_date: values.date,
    passenger_types: values.passengers,
    selections: {
      // Pick up
      [complexDeparture.groupId]: {
        packageItems: {
          [complexDeparture.packageId]: {
            selected: true,
            options: {
              from_route_stop_id: values.pickUp,
            },
          },
        },
      },

      // Drop off
      [complexReturn.groupId]: {
        packageItems: {
          [complexReturn.packageId]: {
            selected: true,
            options: {
              to_route_stop_id: values.dropOff,
            },
          },
        },
      },
    },
  };

  objectScan(['**'], {
    rtn: 'value',
    filterFn: ({ value }) => {
      const isValidGroup =
        value?.type === 'ResourceGroup' &&
        value?.pick === 0 &&
        noDefaultSelection(value);

      if (isValidGroup && isAccommdation(value)) {
        apiData.selections[value.id] = {
          packageItems: {
            [value.package_items[0].id]: {
              selected: true,
              options: {
                bed_requirement_id: values.bedRequirement,
              },
            },
          },
        };
      }
      if (isValidGroup && isRoomPickerResource(value) && values.roomType) {
        apiData.selections[value.id] = {
          packageItems: {
            [values.roomType]: {
              selected: true,
            },
          },
        };
      }
    },
  })(resource);

  // Select departure ferry if available
  if (complexDeparture.resourceGroupId) {
    apiData.selections[complexDeparture.resourceGroupId] = {
      packageItems: {
        [complexDeparture.packageItemId]: {
          selected: true,
        },
      },
    };
  }

  // Select return ferry if available
  if (complexReturn.resourceGroupId) {
    apiData.selections[complexReturn.resourceGroupId] = {
      packageItems: {
        [complexReturn.packageItemId]: {
          selected: true,
        },
      },
    };
  }

  // Select ferry pick up if available
  if (departureFerry?.groupId) {
    apiData.selections[departureFerry.groupId] = {
      packageItems: {
        [departureFerry.packageId]: {
          selected: true,
          options: {
            from_route_stop_id: departureFerry.value,
          },
        },
      },
    };
  }

  // Select ferry drop off if available
  if (returnFerry?.groupId) {
    apiData.selections[returnFerry.groupId] = {
      packageItems: {
        [returnFerry.packageId]: {
          selected: true,
          options: {
            from_route_stop_id: returnFerry.value,
          },
        },
      },
    };
  }

  return apiData;
};

export const getComplexBookableOption = (complexDeparture, complexReturn) => ({
  ...complexDeparture,
  package_options: [
    complexDeparture.package_options,
    complexReturn.package_options,
  ],
});

export const getRoomTypeOptions = (resource) => {
  const { resource_groups: resourceGroups = [] } = resource;
  const roomPicker = resourceGroups.filter(
    (item) => isRoomPickerResource(item) && item.pick === 0
  )[0];
  if (isEmpty(roomPicker)) {
    return null;
  }
  const options = roomPicker.package_items
    .map((item) => ({
      label: item.resource.name,
      value: item.id,
      minPassenger: item.resource.minimum_passengers,
    }))
    .sort(({ minPassenger: a = 0 }, { minPassenger: b = 0 }) => a - b);
  return options;
};

export const getRoomTypeByPassengerCount = (options, passengerCount) => {
  if (isEmpty(options)) {
    return null;
  }
  const roomType = options.find(
    (option) => option.minPassenger === passengerCount
  )?.value;
  if (!isNil(roomType)) {
    return roomType;
  }
  // Some room option's minimum passenger is null, we consider it means 1.
  if (passengerCount === 1) {
    return options.find((option) => isNil(option.minPassenger))?.value;
  }
  return null;
};

export const sortPickUpDropOff = (locations) => {
  const [originPickUps, originDropOffs] = locations;
  const pickUps = cloneDeep(originPickUps);
  const dropOffs = cloneDeep(originDropOffs);
  const { sortLocations = false } =
    window.ecom.craftConfig.bookingPreferences || {};

  if (sortLocations) {
    pickUps?.options.sort((a, b) => a.name.localeCompare(b.name, 'en'));
    dropOffs?.options.sort((a, b) => a.name.localeCompare(b.name, 'en'));
  }

  return [pickUps, dropOffs];
};

export const getMaximumPassenger = ({ valueFromResource, roomCapacity }) => {
  const virtualMaxNumber = 999;
  const arr = [valueFromResource, roomCapacity].map(
    (i) => i || virtualMaxNumber
  );
  return Math.min(...arr);
};

export const generateTitle = ({ pickUps, dropOffs }) => {
  if (pickUps && !dropOffs) {
    return 'Pick-up';
  }
  if (!pickUps && dropOffs) {
    return 'Drop-off';
  }
  return 'Pick-up and drop-off';
};
